import {useTranslation} from "next-i18next";
import {useRouter} from "next/router";
import React, {useEffect, useState} from "react";
import Button from "../common/Button";
import {useQuery, useQueryClient} from "@tanstack/react-query";
import {getBookings, getObject, getSeasonDates} from "../../data/ReactQueries";
import {objectsAttributes} from "../../models/objects";
import {calcNormalRentPrice, getBookedDates} from "../CalculationHelper/CalcHelpers";
import {formatCalendarDate} from "../../helpers/CalendarDate";

interface BuchungAnfrageMobileHeaderProps {
	startDate: Date | null;
	endDate: Date | null;
	objectId: string;
}

function BuchungAnfrageMobileHeader({
	                                    endDate,
	                                    objectId,
	                                    startDate
                                    }: BuchungAnfrageMobileHeaderProps) {
	const router = useRouter();
	const {t} = useTranslation();

	const [lastMinutePricePerWeek, setLastMinutePricePerWeek] = useState(0);
	const [normalPricePerWeek, setNormalPricePerWeek] = useState(0);

	const queryClient = useQueryClient();

	const {data: object} = useQuery(
		["object", objectId],
		() => getObject(objectId),
		{
			onSuccess: (_object: objectsAttributes) => {
				queryClient.setQueryData(["object", objectId], _object);
			}
		}
	);

	const {data: seasonDates, isLoading} = useQuery(
		["seasonDates"],
		getSeasonDates
	);

	useEffect(() => {
		let _startDate = startDate;
		let _endDate = endDate;
		if (object && seasonDates) {
			const [_normalPrice, _specialPrice] = calcNormalRentPrice(
				object,
				seasonDates,
				_startDate,
				_endDate
			);

			if (!_startDate || !_endDate) {
				setNormalPricePerWeek(_normalPrice);
				setLastMinutePricePerWeek(_specialPrice);
			} else {
				setNormalPricePerWeek(_normalPrice + parseInt(object.end_cleaning));
				setLastMinutePricePerWeek(
					_specialPrice > 0
						? _specialPrice + parseInt(object.end_cleaning)
						: _specialPrice
				);
			}
		}
	}, [object, seasonDates, startDate, endDate]);

	const [selectedDatesForMobile, setSelectedDatesForMobile] = useState<{
		start: Date;
		end: Date;
	}>();

	const {data: filtedBookingsByObjectId} = useQuery(
		["bookings", objectId.toString()],
		() => getBookings(objectId.toString()),
		{
			onSuccess(data) {
			}
		}
	);

	const bookedDates: { start: Date; end: Date }[] | undefined = getBookedDates(
		filtedBookingsByObjectId
	);

	const MAX_RANGE_LENGTH = 7; // Maximum length of date range in days

	interface Booking {
		from_date: number;
		to_date: number;
	}

	interface DateRange {
		start: Date;
		end: Date;
	}

	function findFreeDateRange(
		bookedDates: DateRange[] = []
	): DateRange | undefined {
		// set currentDate
		const currentDate = new Date();

		const oneDayInMilliseconds = 1 * 24 * 60 * 60 * 1000;
		let startDate = currentDate;
		let startDayPlusOne = new Date(
			currentDate.getTime() + oneDayInMilliseconds
		);
		let endDate = new Date(startDate.getTime()); // Set initial end date as tomorrow

		// Loop through each day starting from the current date until you find a free day
		let i = 0;
		while (i < 365) {
			const isFree = !bookedDates.some((booking) => {
				return (
					(startDate.getTime() >= booking.start.getTime() &&
						startDate.getTime() <= booking.end.getTime()) ||
					(startDayPlusOne.getTime() >= booking.start.getTime() &&
						startDayPlusOne.getTime() <= booking.end.getTime())
				);
			});

			// If the current date is free, break out of the loop
			if (isFree) {
				break;
			}

			// If the current date is not free, increment the start date by 1 day
			startDate = new Date(startDate.getTime() + oneDayInMilliseconds);
			startDayPlusOne = new Date(
				startDayPlusOne.getTime() + oneDayInMilliseconds
			);
			i++;
		}

		// // Set the end date to the maximum range length from the start date
		endDate = new Date(startDate.getTime());

		// Check if the end date is free. If not, decrement the end date until a free day is found within the maximum range length
		i = 0;
		while (i < MAX_RANGE_LENGTH) {
			const _endDate = new Date(endDate.getTime() + oneDayInMilliseconds);

			const isFree = !bookedDates.some((booking) => {
				return (
					_endDate.getTime() >= booking.start.getTime() &&
					_endDate.getTime() <= booking.end.getTime()
				);
			});

			if (!isFree) {
				break;
			}

			endDate = _endDate;
			i++;
		}

		// Return the range of consecutive free days
		return {start: startDate, end: endDate};
	}

	const weeklyPrice =
		lastMinutePricePerWeek > 0 ? lastMinutePricePerWeek : normalPricePerWeek;

	let tmpStartDate = router.query.from_date
		? new Date(parseInt(router.query.from_date as string) * 1000)
		: new Date();
	let tmpEndDate = router.query.to_date
		? new Date(parseInt(router.query.to_date as string) * 1000)
		: new Date();
	if (
		(!router.query.from_date || !router.query.to_date) &&
		bookedDates &&
		bookedDates.length > 0
	) {
		//console.log("Got booked dates of object ", bookedDates);
		const freeDateRange = findFreeDateRange(bookedDates);
		if (freeDateRange) {
			//console.log("Got free date ranges ", freeDateRange);
			tmpStartDate = freeDateRange.start;
			tmpEndDate = freeDateRange.end;
		}
	}

	const noDateSelected = !startDate && !endDate;

	return (
		<div className="xl:hidden border-t-2 border-leo-divider-gray-2 text-black flex flex-row items-center justify-between shadow-lg py-3 px-5 mb-3">
			<div className="flex flex-col">
				<p className="text-[14px]">
					{t("components.buchungAnfrageMobileHeader.priceForAWeek")}
				</p>
				<p className="text-[20px] text-leo-blue font-bold">
					{t(noDateSelected ? "common.priceFrom" : "common.price", {price: weeklyPrice})}
				</p>
			</div>
			<Button
				text={t("components.buchungAnfrageMobileHeader.sendBookingRequest")}
				transparent={false}
				border={false}
				className="rounded-[10px] text-[12px] font-bold leading-normal hidden"
				height={"h-[43px]"}
				minWidth={"min-w-[150px]"}
				padding={"px-0"}
				textTransform={"uppercase"}
				onClick={() => {
					router.push({
						pathname: "/vermietung/buchungsanfrage",
						query: {
							startDate: formatCalendarDate(tmpStartDate),
							endDate: formatCalendarDate(tmpEndDate),
							adultCount: 1,
							kidsCount: 0,
							objectId: objectId
						}
					});
				}}
			/>
		</div>
	);
}

export default BuchungAnfrageMobileHeader;
